import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import type { FacultyClassDetail } from '../types';

export type EditClassPayload = {
  term: string;
  class_name: string;
  course_code: string;
  section_code: string;
  start_date?: string;
  end_date?: string;
};

interface EditClassDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: EditClassPayload) => void;
  isSubmitting: boolean;
  classDetail: FacultyClassDetail;
}

function EditClassDialog({ open, onClose, onSubmit, isSubmitting, classDetail }: EditClassDialogProps) {
  const [term, setTerm] = useState('');
  const [className, setClassName] = useState('');
  const [courseCode, setCourseCode] = useState('');
  const [sectionCode, setSectionCode] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (!open) return;

    setTerm(classDetail.term || '');
    setClassName(classDetail.class_name || '');
    setCourseCode(classDetail.course_code || '');
    setSectionCode(classDetail.section_code || '');
    setStartDate(classDetail.start_date || '');
    setEndDate(classDetail.end_date || '');
    // Only reseed the form when the dialog opens, not when a background refetch updates the detail.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = () => {
    onSubmit({
      term,
      class_name: className,
      course_code: courseCode,
      section_code: sectionCode,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Edit Class</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Term" value={term} onChange={(event) => setTerm(event.target.value)} required fullWidth />
          <TextField label="Class Name" value={className} onChange={(event) => setClassName(event.target.value)} required fullWidth />
          <TextField label="Course Code" value={courseCode} onChange={(event) => setCourseCode(event.target.value)} required fullWidth />
          <TextField label="Section Code" value={sectionCode} onChange={(event) => setSectionCode(event.target.value)} required fullWidth />
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditClassDialog;
